import {
  type ControlBulkDraftRow,
  type ControlDraftRowMergeOptions,
  type ControlDraftTableField,
  controlColumnMapsToField,
  effectiveControlBackendErrorsForRow,
  normalizeControlColumnLabel,
} from './controlDraftValidation.ts';
import { assertionOptions } from '../../utils/controlConstants.js';

/** First allowed assertion in control bulk template / backend enum (existence). */
const CONTROL_BULK_IMPORT_DEFAULT_ASSERTION =
  assertionOptions[0]?.value ?? 'existence';

/**
 * Import defaults for bulk-upload columns shown under Validation Issues.
 * Matches skeptics-backend: constants.SOXControl*DefaultDB and excel template samples.
 */
export const CONTROL_BULK_IMPORT_FIELD_DEFAULTS = {
  objective: '',
  control_type: 'manual',
  frequency: '',
  significance: 'non-key',
  classification: 'business',
  control_nature: '',
  mrc_control: false,
  assertions: [CONTROL_BULK_IMPORT_DEFAULT_ASSERTION],
  department: '',
  external_auditor_reliance: false,
} as const;

export type ControlBulkImportPayloadKey = keyof typeof CONTROL_BULK_IMPORT_FIELD_DEFAULTS;

const EDITABLE_UI_FIELDS: ControlDraftTableField[] = ['clientControlId', 'title', 'controlWordingText'];

const COLUMN_NORMALIZED_TO_PAYLOAD_KEY: Record<string, ControlBulkImportPayloadKey> = {
  controlobjective: 'objective',
  controltype: 'control_type',
  frequency: 'frequency',
  significance: 'significance',
  classification: 'classification',
  controlnature: 'control_nature',
  mrccontrol: 'mrc_control',
  assertions: 'assertions',
  department: 'department',
  externalauditorreliance: 'external_auditor_reliance',
};

function isOrphanValidationColumn(column: string): boolean {
  return !EDITABLE_UI_FIELDS.some((field) => controlColumnMapsToField(column, field));
}

export function controlImportPayloadKeyForErrorColumn(column: string): ControlBulkImportPayloadKey | null {
  if (!isOrphanValidationColumn(column)) return null;

  const normalized = normalizeControlColumnLabel(column);
  const direct = COLUMN_NORMALIZED_TO_PAYLOAD_KEY[normalized];
  if (direct) return direct;

  if (normalized.includes('objective')) return 'objective';
  if (normalized.includes('controltype')) return 'control_type';
  if (normalized.includes('frequency')) return 'frequency';
  if (normalized.includes('significance')) return 'significance';
  if (normalized.includes('classification')) return 'classification';
  if (normalized.includes('controlnature')) return 'control_nature';
  if (normalized.includes('mrccontrol') || normalized.includes('mrc')) return 'mrc_control';
  if (normalized.includes('assertions')) return 'assertions';
  if (normalized.includes('department')) return 'department';
  if (normalized.includes('externalauditor')) return 'external_auditor_reliance';

  return null;
}

export function buildControlImportWireRow(
  row: ControlBulkDraftRow,
  getFieldValue: (row: ControlBulkDraftRow, field: ControlDraftTableField) => unknown,
  options: ControlDraftRowMergeOptions
): Record<string, unknown> {
  const base =
    row.stagedRow && typeof row.stagedRow === 'object'
      ? { ...row.stagedRow }
      : ({} as Record<string, unknown>);

  base.client_control_id = String(getFieldValue(row, 'clientControlId') ?? '').trim();
  base.title = String(getFieldValue(row, 'title') ?? '').trim();
  base.control_wording_text = String(getFieldValue(row, 'controlWordingText') ?? '').trim();

  const errors = effectiveControlBackendErrorsForRow(row, getFieldValue, options);
  const keysToReplace = new Set<ControlBulkImportPayloadKey>();

  for (const err of errors) {
    const column = String(err.column ?? '').trim();
    if (!column || !isOrphanValidationColumn(column)) continue;
    const payloadKey = controlImportPayloadKeyForErrorColumn(column);
    if (payloadKey) keysToReplace.add(payloadKey);
  }

  for (const key of keysToReplace) {
    base[key] = CONTROL_BULK_IMPORT_FIELD_DEFAULTS[key];
  }

  return base;
}
